import React, { useCallback, useEffect, useMemo, useState } from 'react';

import alert from '../../helpers/alert';
import extractErrorMessage from '../../utils/extractErrorMessage';
import styles from './sheetSync.module.css';
import LinkEditor from './LinkEditor';
import { deleteSheetLink, getSheetLinks, saveSheetLinks } from '../../api/prices';
import { LINK_SOURCE, LINK_STATUS, deltaText, formatShort, linkNeedsAttention, money } from './sheetSyncFormat';

const FILTERS = [
  ['all', 'Все'],
  ['attention', 'Требуют внимания'],
  ['manual', 'Привязаны вручную'],
  ['ignored', 'Не синхронизируются'],
];

const COLUMNS = 8;

const matches = (v, filter, query) => {
  if (filter === 'attention' && !linkNeedsAttention(v.status)) return false;
  if (filter === 'manual' && v.source !== 'manual') return false;
  if (filter === 'ignored' && v.status !== 'ignored') return false;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [v.sheetKey, v.brand, v.model, v.year, v.trim, ...v.targets.map((t) => t.label)].join(' ').toLowerCase();
  return hay.includes(q);
};

// Вкладка «Привязки»: все строки таблицы, привязанные к правилам приказа, одной плотной таблицей.
// Здесь их можно проверить, поменять правило или снять привязку, если что-то привязалось не туда
const SheetLinks = ({ marks = [] }) => {
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [editKey, setEditKey] = useState(null);
  // Снятие привязки возвращает строку в «Проблемы» — второй клик подтверждает, как у остальных опасных кнопок
  const [confirmKey, setConfirmKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getSheetLinks()
      .then((res) => setLinks(res.data || []))
      .catch((err) => {
        setLinks([]);
        alert.error(extractErrorMessage(err) || 'Не удалось загрузить привязки');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (confirmKey == null) return undefined;
    const timer = setTimeout(() => setConfirmKey(null), 5000);
    return () => clearTimeout(timer);
  }, [confirmKey]);

  const visible = useMemo(() => (links || []).filter((v) => matches(v, filter, query)), [links, filter, query]);
  const attention = (links || []).filter((v) => linkNeedsAttention(v.status)).length;

  const save = async (v, payload) => {
    setBusy(true);
    try {
      const res = await saveSheetLinks(payload);
      if (res.data.errors?.length) {
        alert.error(res.data.errors.join('; '));
        return;
      }
      alert.success(`Привязка ${v.sheetKey} изменена и сохранена. Цены пока не менялись: проверьте «Таблицу» и примените изменения.`);
      setEditKey(null);
      load();
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось сохранить привязку');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (v) => {
    if (confirmKey !== v.sheetKey) {
      setConfirmKey(v.sheetKey);
      return;
    }
    setConfirmKey(null);
    setBusy(true);
    try {
      await deleteSheetLink(v.sheetKey);
      alert.success(
        v.status === 'ignored'
          ? `${v.sheetKey} снова синхронизируется`
          : `Привязка ${v.sheetKey} снята. Строка вернулась к сопоставлению по названию и, если не найдёт пару, появится в «Проблемах».`
      );
      load();
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось снять привязку');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !links) return <div className={styles.empty}>Загрузка…</div>;

  return (
    <>
      <details className={styles.help}>
        <summary>Что это за вкладка</summary>
        <ul>
          <li>
            Привязка говорит системе, к какому правилу приказа относится строка таблицы (по её ID, например CAR-077). Название в
            таблице после этого можно писать как угодно.
          </li>
          <li>
            «Изменить» — выбрать другое правило (или несколько, если в каталоге двойник комплектации). Изменение сохраняется сразу;
            цены оно не трогает — новая цена попадёт в «Таблица → К применению».
          </li>
          <li>«Отвязать» — снять привязку: строка вернётся к сопоставлению по названию и при неудаче станет проблемой.</li>
          <li>Если текст строки в таблице изменился после привязки, привязка перестаёт действовать, пока вы её не подтвердите заново.</li>
        </ul>
      </details>

      <div className={styles.top}>
        <input
          className={styles.filter}
          placeholder="Поиск: ID, модель, комплектация"
          aria-label="Поиск по привязкам"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className={styles.filterSelect}
          aria-label="Фильтр привязок"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {FILTERS.map(([key, title]) => (
            <option key={key} value={key}>
              {title}
              {key === 'attention' ? ` (${attention})` : ''}
            </option>
          ))}
        </select>
        <span className={styles.muted}>
          Показано {visible.length} из {(links || []).length}
        </span>
      </div>

      {visible.length === 0 ? (
        <div className={styles.empty}>{(links || []).length === 0 ? 'Привязок ещё нет' : 'По условиям ничего не найдено'}</div>
      ) : (
        <table className={styles.linksTable}>
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '21%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '14%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>ID</th>
              <th>Строка таблицы</th>
              <th className={styles.num}>Цена в таблице</th>
              <th>Привязана к правилу</th>
              <th className={styles.num}>Цена правила</th>
              <th>Статус</th>
              <th>Кем и когда</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((v) => {
              const st = LINK_STATUS[v.status] || { label: v.status, hint: '' };
              return (
                <React.Fragment key={v.sheetKey}>
                  <tr>
                    <td className={styles.cellKey}>{v.sheetKey}</td>
                    <td className={styles.ellipsis} title={v.brand != null ? `${v.brand} ${v.model} ${v.year} · ${v.trim}` : undefined}>
                      {v.brand != null ? `${v.brand} ${v.model} ${v.year} · ${v.trim}` : <span className={styles.muted}>строки нет в таблице</span>}
                    </td>
                    <td className={styles.num}>{money(v.price)}</td>
                    <td className={styles.ellipsisCell}>
                      {v.status === 'ignored' ? (
                        <span className={styles.muted}>не синхронизируется</span>
                      ) : (
                        v.targets.map((t) => (
                          <span className={styles.targetLine} key={t.id} title={`${t.mark} ${t.label}`}>
                            <span className={styles.muted}>{t.mark}</span> {t.label}
                          </span>
                        ))
                      )}
                    </td>
                    <td className={styles.num}>
                      {v.status !== 'ignored' &&
                        v.targets.map((t) => (
                          <span className={styles.targetLine} key={t.id}>
                            {t.rulePrice == null ? (
                              <span className={styles.warn}>создастся</span>
                            ) : (
                              <>
                                {money(t.rulePrice)}
                                {v.price != null && t.rulePrice !== v.price && (
                                  <span className={`${styles.warn} ${styles.diffLine}`}>в таблице {deltaText(t.rulePrice, v.price)}</span>
                                )}
                              </>
                            )}
                          </span>
                        ))}
                    </td>
                    <td>
                      <span className={`${styles.status} ${styles[`status_${v.status}`] || ''}`} title={st.hint}>
                        {st.label}
                      </span>
                    </td>
                    <td className={`${styles.sourceCell} ${styles.ellipsis}`} title={`${LINK_SOURCE[v.source] || v.source}${v.author && v.author !== 'авто' ? ` · ${v.author}` : ''} · ${formatShort(v.createdAt)}`}>
                      {LINK_SOURCE[v.source] || v.source} · {formatShort(v.createdAt)}
                    </td>
                    <td className={styles.cellActions}>
                      {v.status !== 'ignored' && (
                        <button
                          type="button"
                          className={styles.mini}
                          disabled={busy}
                          title="Выбрать другое правило для этой строки"
                          onClick={() => setEditKey(editKey === v.sheetKey ? null : v.sheetKey)}
                        >
                          Изменить
                        </button>
                      )}
                      <button
                        type="button"
                        className={confirmKey === v.sheetKey ? styles.miniDanger : styles.mini}
                        disabled={busy}
                        title={
                          v.status === 'ignored'
                            ? 'Строка снова будет участвовать в синхронизации'
                            : 'Снять привязку: строка вернётся к сопоставлению по названию'
                        }
                        onClick={() => unlink(v)}
                      >
                        {v.status === 'ignored'
                          ? confirmKey === v.sheetKey
                            ? 'Вернуть?'
                            : 'Вернуть в синхронизацию'
                          : confirmKey === v.sheetKey
                          ? 'Точно отвязать?'
                          : 'Отвязать'}
                      </button>
                    </td>
                  </tr>
                  {editKey === v.sheetKey && (
                    <tr>
                      <td colSpan={COLUMNS} className={styles.editorRowCell}>
                        <LinkEditor
                          sheetKey={v.sheetKey}
                          problem={{ brand: v.brand, year: v.year }}
                          marks={marks}
                          initial={v.targets}
                          busy={busy}
                          saveText="Сохранить изменения"
                          onSave={(payload) => save(v, payload)}
                          onCancel={() => setEditKey(null)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
};

export default SheetLinks;
